import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from '../../utils/AxiosHelper';
import API from '../../modules/settings/APIs';
import {useAppSelector} from '../hooks';

export interface NotificationTemplate {
  locale: string;
  subject: string;
  body: string;
  custom: boolean;
  version: number | null;
}

export interface NotificationOption {
  code: string;
  value: string;
  defaultValue: string;
  allowedValues: string[];
}

export const MASTER_RECIPIENT_MODE = {
  none: 'NONE',
  optional: 'OPTIONAL',
  required: 'REQUIRED',
} as const;

export type MasterRecipientMode = (typeof MASTER_RECIPIENT_MODE)[keyof typeof MASTER_RECIPIENT_MODE];

export interface NotificationEvent {
  code: string;
  enabled: boolean;
  hasTemplate: boolean;
  custom: boolean;
  option: NotificationOption | null;
  extraRecipients: string[];
  masterRecipientMode: MasterRecipientMode;
  masterRecipients: string[];
  templates: NotificationTemplate[];
  templateTokens: string[];
}

export interface NotificationSection {
  code: string;
  tone: string;
  events: NotificationEvent[];
}

export interface NotificationSettings {
  channel: string;
  emailServerConfigured: boolean;
  defaultLocale: string;
  supportedLocales: string[];
  sections: NotificationSection[];
}

/** Request payload for `PUT /notification-settings` — one row per event. */
export interface NotificationEventState {
  code: string;
  enabled: boolean;
  optionValue: string | null;
  extraRecipients: string[];
  masterRecipients: string[];
}

export interface SaveTemplatePayload {
  subject: string;
  body: string;
  version: number | null;
}

const queryKey = ['settings', 'notifications'] as const;

/** Replaces one event, in place, across every section — used after a
 * per-locale template save/reset returns a single refreshed event. */
const replaceEvent = (
  settings: NotificationSettings,
  event: NotificationEvent,
): NotificationSettings => ({
  ...settings,
  sections: settings.sections.map((section) => ({
    ...section,
    events: section.events.map((current) => (current.code === event.code ? event : current)),
  })),
});

export const useNotificationSettings = () => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<NotificationSettings> => {
      const response = await apiRequest.get(API.settings.notifications.get);
      return response.data?.data;
    },
    enabled: isAuthenticated && !!token,
  });
};

export const useSaveNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: NotificationEventState[]): Promise<NotificationSettings> => {
      const response = await apiRequest.put(API.settings.notifications.save, {items});
      return response.data?.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });
};

export const useSaveNotificationTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
                         code,
                         locale,
                         payload,
                       }: {
      code: string;
      locale: string;
      payload: SaveTemplatePayload;
    }): Promise<NotificationEvent> => {
      const response = await apiRequest.put(API.settings.notifications.template(code, locale), payload);
      return response.data?.data;
    },
    onSuccess: (event) => {
      queryClient.setQueryData<NotificationSettings | undefined>(queryKey, (current) =>
        current ? replaceEvent(current, event) : current,
      );
    },
  });
};

export const useResetNotificationTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({code, locale}: { code: string; locale: string }): Promise<NotificationEvent> => {
      const response = await apiRequest.delete(API.settings.notifications.resetTemplate(code, locale));
      return response.data?.data;
    },
    onSuccess: (event) => {
      queryClient.setQueryData<NotificationSettings | undefined>(queryKey, (current) =>
        current ? replaceEvent(current, event) : current,
      );
    },
  });
};
